import React, { useMemo } from 'react';

import Locale from '../Locale';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';
import styles from './DatePicker.styles';
import { translate } from '../../helpers/i18n';

const useStyles = makeStyles()(styles);

const DatePicker = (props = {}) => {
    const { classes } = useStyles();
    const {
        autoOk = true,
        DatePickerProps: { maxDate, minDate, InputProps: { size } = {}, ...DatePickerProps } = {},
        disabled,
        error,
        format = 'MM/DD/YYYY',
        fullWidth,
        label,
        localeCode,
        helperText,
        id,
        InputLabelProps,
        onChange,
        placeholder,
        required,
        useUTC,
        value,
    } = props;

    const pickerValue = useMemo(
        () => (value || Number.isNaN(value) ? (useUTC ? moment.utc(value) : moment(value)) : null),
        [useUTC, value]
    );

    const textFieldProps = useMemo(
        () => ({
            ...(id && { id }),
            InputProps: {
                notched: !!label,
            },
            InputLabelProps,
            size,
            variant: 'outlined',
            error: error || undefined,
            fullWidth,
            classes: {
                root: classes.textField,
            },
            color: 'secondary',
            ...(placeholder && { placeholder: translate(placeholder) }),
            helperText: helperText && <Locale path={helperText} />,
            required,
        }),
        [
            classes.textField,
            error,
            fullWidth,
            helperText,
            id,
            InputLabelProps,
            label,
            localeCode,
            placeholder,
            required,
            size,
        ]
    );

    return (
        <MuiDatePicker
            autoOk={autoOk}
            format={format}
            label={<Locale path={label} />}
            onChange={selectedDate => onChange(selectedDate ? selectedDate.valueOf() : null)}
            value={pickerValue}
            slotProps={{ textField: textFieldProps }}
            disabled={disabled}
            {...DatePickerProps}
            minDate={minDate ? moment(minDate) : undefined}
            maxDate={maxDate ? moment(maxDate) : undefined}
        />
    );
};

DatePicker.propTypes = {
    autoOk: PropTypes.bool,
    DatePickerProps: PropTypes.object,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    format: PropTypes.string,
    fullWidth: PropTypes.bool,
    label: PropTypes.string,
    localeCode: PropTypes.string,
    helperText: PropTypes.string,
    id: PropTypes.string,
    InputLabelProps: PropTypes.object,
    onChange: PropTypes.func,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    required: PropTypes.bool,
    useUTC: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DatePicker;
